import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useRef } from "react";

import { faExclamationCircle, faGripLines, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";

//hooks
import {
	ArrayFieldState,
	ButtonStandard,
	FormField,
	FormInputText,
	FormList,
	Icon,
	Text,
	useKeyPress,
	useStableList,
} from "@performyard/py-components";
//utils
import { Checkbox } from "@performyard/py-components/src/components/atoms/Checkbox";
import SortableDragNDrop from "@performyard/py-components/src/components/molecules/SortableDragNDrop";

//components
import IconButton from "../../../../../../../components/Button/IconButton";
import Spacer from "../../../../../../../components/Spacer";

import { EditQuestionBlockProps } from "../../QuestionBlock";

type Option = string;
const MAX_CHOICES = 20;

const MultipleChoiceEntries = ({ field, index }: { field: ArrayFieldState<Option>; index: number }) => {
	const { value, pushValue } = field;

	const [ref] = useAutoAnimate();
	const containerRef = useRef<HTMLDivElement>(null);

	// Use stable list hook for ID management
	const { stableList: items, shiftItems } = useStableList({ list: value || [], onShift: field.shiftValue });

	useKeyPress({
		key: "Enter",
		onPress: (e) => {
			e.preventDefault();
			e.stopPropagation();
			//get add button and click it
			const addButton = document.getElementById(`add-option-${index}`);
			if (addButton) {
				addButton.click();
			}
		},
		containerRef: containerRef as React.RefObject<HTMLElement>,
	});

	return (
		<div ref={containerRef}>
			{value?.length > 0 && (
				<SortableDragNDrop
					ref={ref}
					items={items}
					onDragEnd={(event) => {
						const { active, over } = event;
						if (active.id !== over?.id) {
							// Find which indices moved
							const oldIndex = items.findIndex((item) => item._id === active.id);
							const newIndex = items.findIndex((item) => item._id === over?.id);

							// Shift the form value
							shiftItems(oldIndex, newIndex);
						}
					}}
					restrictToParentElement
					restrictToVerticalAxis
					listClassName="flex flex-col gap-2"
					useUnorderedList
					renderItem={(item, { dragHandlers }) => {
						const currentIndex = items.findIndex((i) => i._id === item._id);
						return (
							<div id="multiple-choice-row" className="flex gap-2" data-testid="multiple-choice-row">
								<div
									{...dragHandlers.attributes}
									{...dragHandlers.listeners}
									className="flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center border-r"
								>
									<Icon
										color="neutral-7"
										dangerouslySetClassName="text-neutral-7 hover:text-neutral-9 active:cursor-grabbing"
										icon={faGripLines}
									/>
								</div>

								<div className="flex-grow">
									<FormInputText
										id={`choices[${currentIndex}]`}
										name={`choices[${currentIndex}]`}
										placeholder={`Answer Option ${currentIndex + 1}`}
									/>
								</div>
								{field?.value?.length > 1 && (
									<IconButton
										style={{ width: 20 }}
										color="gray"
										icon={faXmarkCircle}
										onClick={() => field.removeValue(currentIndex)}
										testId="delete-option"
										aria-label="Remove option"
									/>
								)}
							</div>
						);
					}}
				/>
			)}

			{field.value?.length >= MAX_CHOICES && (
				<div className="flex items-center gap-2 pt-2">
					<Icon size={16} color="red-6" icon={faExclamationCircle} />
					<Text size="caption" weight="semi-bold" color="red-6">
						You have reached the maximum number of options
					</Text>
				</div>
			)}
			<Spacer height={2} />

			<div className="flex w-full justify-end">
				<ButtonStandard
					id={`add-option-${index}`}
					type="button"
					iconLeft={faPlus}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (field.value?.length >= MAX_CHOICES) {
							return;
						}
						pushValue("");

						// wait 10 ms and then focus on the last input to wait for render
						setTimeout(() => {
							const lastInput = document.getElementById(`choices[${field.value?.length}]`);
							if (lastInput) {
								lastInput.focus();
							}
						}, 10);
					}}
					disabled={field.value?.length >= MAX_CHOICES}
				>
					Add Option
				</ButtonStandard>
			</div>
		</div>
	);
};

const MultipleChoiceEdit = ({ index }: EditQuestionBlockProps) => {
	return (
		<div id="multiple-choice-section">
			<Text overrideElement="h6" size="paragraph-3" weight="semi-bold">
				Answer Options
			</Text>
			<Spacer height={1} />
			<FormField<string>
				id="type"
				label={<Text size="paragraph-3">Allow for selecting multiple answers</Text>}
				labelPosition="right"
				input={(field) => (
					<div className="flex items-center gap-2">
						<Checkbox
							id="type"
							checked={field.value === "multiple-select"}
							onCheckedChange={() =>
								field.onChange(
									field.value === "multiple-select" ? "multiple-choice" : "multiple-select"
								)
							}
						/>
					</div>
				)}
			/>
			<Spacer height={2} />
			<FormList<Option> id="choices" input={(field) => <MultipleChoiceEntries field={field} index={index} />} />
		</div>
	);
};

export default MultipleChoiceEdit;
